import { CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMarkets } from '@/api/domains/markets';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { PromiseDateMarketSection } from './PromiseDateMarketSection';

export function PromiseDatePage() {
  const { t } = useTranslation('widgets');
  const tCommon = useTranslation('common').t;
  usePageTitle(t('promiseDate.title'));

  const navigate = useNavigate();
  const marketsQuery = useMarkets();
  const markets = marketsQuery.data ?? [];

  return (
    <div className="mx-auto flex w-full max-w-[944px] flex-col gap-9">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-6">
          <h2 className="text-4xl font-medium tracking-[-0.9px] text-black">
            {t('promiseDate.title')}
          </h2>
          {/* Video walkthrough is not published yet (placeholder href). */}
          <a
            href="#"
            className="inline-flex h-9 items-center justify-center rounded-[10px] border border-black px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-accent"
          >
            {t('promiseDate.watchVideo')}
          </a>
        </div>
        <p className="max-w-[695px] text-base text-muted-foreground">
          {t('promiseDate.description')}
        </p>
      </div>

      {marketsQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : marketsQuery.isError ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed p-4">
          <p className="text-sm text-muted-foreground">{tCommon('errors.unknown')}</p>
          <Button variant="outline" size="sm" onClick={() => void marketsQuery.refetch()}>
            {tCommon('actions.retry')}
          </Button>
        </div>
      ) : markets.length === 0 ? (
        <EmptyState onDefine={() => navigate('/markets')} />
      ) : (
        <div className="flex flex-col">
          {markets.map((market) => (
            <PromiseDateMarketSection key={market.id} market={market} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onDefine }: { onDefine: () => void }) {
  const { t } = useTranslation('widgets');
  return (
    <div className="flex flex-col items-center justify-center rounded-[4px] bg-[#f5f5f5] p-6">
      <div className="flex w-[422px] max-w-full flex-col items-center gap-[13px]">
        <div className="flex size-[68px] items-center justify-center rounded-[12px] bg-[#e4e4e4]">
          <CalendarClock className="size-8 text-[#525252]" />
        </div>
        <p className="w-full text-center text-xl font-medium tracking-[-0.6px] text-black">
          {t('promiseDate.empty.title')}
        </p>
        <p className="w-full text-center text-sm text-muted-foreground">
          {t('promiseDate.empty.description')}
        </p>
        <Button
          type="button"
          onClick={onDefine}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {t('promiseDate.empty.action')}
        </Button>
      </div>
    </div>
  );
}
